import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import { useUniversalAuth } from '../../contexts/UniversalAuthContext';
import ArborescenceLogo from '../../components/ArborescenceLogo';
import { Search, Filter, UserPlus, ArrowRight, Mail, Phone, MapPin, CheckCircle, XCircle, Clock, TrendingUp } from 'lucide-react';
import '../../styles/admin/Leads.css';

const Leads = () => {
  const navigate = useNavigate();
  const { logout } = useUniversalAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchLeads();
    fetchStats();
  }, [statusFilter, sourceFilter]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.statut = statusFilter;
      if (sourceFilter !== 'all') params.source = sourceFilter;

      const response = await api.get('/api/leads', { params });
      
      setLeads(response.data.leads || []);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        navigate('/auth/login');
      } else {
        console.error('Erreur lors du chargement des leads:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/leads/stats/summary');
      setStats(response.data);
    } catch (error) {
      console.error('Erreur stats:', error);
    }
  };

  const handleConvertToClient = async (lead) => {
    if (!window.confirm(`Convertir "${lead.prenom} ${lead.nom}" en client?`)) {
      return;
    }

    try {
      const response = await api.post(
        '/api/leads/convert-to-client',
        {
          lead_id: lead.id,
          type_client: 'residentiel'
        }
      );

      alert(`✅ ${lead.prenom} ${lead.nom} a été converti en client!\nNuméro client: ${response.data.client.numero_client}`);
      
      // Rafraîchir la liste
      fetchLeads();
      fetchStats();
    } catch (error) {
      alert('❌ Erreur lors de la conversion');
      console.error(error);
    }
  };

  const getStatutBadgeClass = (statut) => {
    const classes = {
      'nouveau': 'badge-nouveau',
      'a_qualifier': 'badge-a-qualifier',
      'qualifie': 'badge-qualifie',
      'converti': 'badge-converti',
      'perdu': 'badge-perdu',
      'a_relancer': 'badge-a-relancer'
    };
    return classes[statut] || 'badge-default';
  };

  const getStatutLabel = (statut) => {
    const labels = {
      'nouveau': 'Nouveau',
      'a_qualifier': 'À qualifier',
      'qualifie': 'Qualifié',
      'converti': 'Converti',
      'perdu': 'Perdu',
      'a_relancer': 'À relancer'
    };
    return labels[statut] || statut;
  };

  const getSourceLabel = (source) => {
    const labels = {
      'site_web': 'Site web',
      'facebook': 'Facebook',
      'google': 'Google',
      'referral': 'Référence',
      'autre': 'Autre'
    };
    return labels[source] || source;
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.courriel.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.numero_lead.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  if (loading) {
    return <div className="admin-loading">Chargement des leads...</div>;
  }

  return (
    <div className="leads-page">
      <div className="leads-header">
        <div className="leads-header-content">
          <Link to="/admin/erp" className="back-link">
            ← Retour au dashboard
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
            <ArborescenceLogo size="small" showText={false} />
            <div>
              <h1>🎯 Gestion des Leads</h1>
              <p>Prospects et opportunités commerciales</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="leads-stats">
          <div className="stat-card">
            <div className="stat-icon nouveau">
              <Clock size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.par_statut.nouveau}</div>
              <div className="stat-label">Nouveaux</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon a-qualifier">
              <Filter size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.par_statut.a_qualifier}</div>
              <div className="stat-label">À qualifier</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon qualifie">
              <CheckCircle size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.par_statut.qualifie}</div>
              <div className="stat-label">Qualifiés</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon converti">
              <TrendingUp size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.taux_conversion}%</div>
              <div className="stat-label">Taux conversion</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="leads-filters">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Rechercher par nom, email, numéro..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">Tous les statuts</option>
          <option value="nouveau">Nouveau</option>
          <option value="a_qualifier">À qualifier</option>
          <option value="qualifie">Qualifié</option>
          <option value="converti">Converti</option>
          <option value="perdu">Perdu</option>
        </select>

        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">Toutes les sources</option>
          <option value="site_web">Site web</option>
          <option value="facebook">Facebook</option>
          <option value="google">Google</option>
          <option value="referral">Référence</option>
        </select>
      </div>

      {/* Leads Table */}
      <div className="leads-table-container">
        <table className="leads-table">
          <thead>
            <tr>
              <th>Numéro</th>
              <th>Nom</th>
              <th>Contact</th>
              <th>Localisation</th>
              <th>Source</th>
              <th>Services</th>
              <th>Statut</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredLeads.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>
                  Aucun lead trouvé
                </td>
              </tr>
            ) : (
              filteredLeads.map((lead) => (
                <tr 
                  key={lead.id}
                  onClick={() => navigate(`/admin/leads/${lead.id}`)}
                  style={{ cursor: 'pointer' }}
                  className="lead-row-clickable"
                >
                  <td>
                    <span className="lead-numero">{lead.numero_lead}</span>
                  </td>
                  <td>
                    <div className="lead-name">
                      <strong>{lead.prenom} {lead.nom}</strong>
                      {lead.superficie_terrain && (
                        <span className="superficie-badge">
                          {lead.superficie_terrain.toLocaleString()} pi²
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="lead-contact">
                      <div className="contact-item">
                        <Mail size={14} />
                        <span>{lead.courriel}</span>
                      </div>
                      {lead.telephone && (
                        <div className="contact-item">
                          <Phone size={14} />
                          <span>{lead.telephone}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="lead-location">
                      <MapPin size={14} />
                      <span>{lead.ville || lead.adresse_complete}</span>
                    </div>
                  </td>
                  <td>
                    <span className="source-badge">{getSourceLabel(lead.source)}</span>
                  </td>
                  <td>
                    <div className="services-list">
                      {lead.services_souhaites && lead.services_souhaites.length > 0 ? (
                        lead.services_souhaites.slice(0, 2).map((service, idx) => (
                          <span key={idx} className="service-tag">{service}</span>
                        ))
                      ) : (
                        <span style={{ color: '#999' }}>-</span>
                      )}
                      {lead.services_souhaites && lead.services_souhaites.length > 2 && (
                        <span className="service-tag">+{lead.services_souhaites.length - 2}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatutBadgeClass(lead.statut)}`}>
                      {getStatutLabel(lead.statut)}
                    </span>
                  </td>
                  <td>
                    <span className="date-text">
                      {new Date(lead.date_creation).toLocaleDateString('fr-CA')}
                    </span>
                  </td>
                  <td>
                    {lead.statut === 'converti' ? (
                      <span className="converted-label">
                        <CheckCircle size={16} />
                        Converti
                      </span>
                    ) : (
                      <button
                        className="btn-convert"
                        onClick={() => handleConvertToClient(lead)}
                        title="Convertir en client"
                      >
                        <UserPlus size={16} />
                        Convertir en client
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Leads;
